import React from 'react'
import { StatusCategory } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import Spinner from '@divvy-web/skylab.spinner'
import Tag from '@divvy-web/skylab.tag'

import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { formatDate, getDashboardStatusTagProps } from '@sputnik/pages/DashboardPages/utils'
import { PRODUCT_CONST_STRINGS } from '@sputnik/resources/constants'

import OverviewLineItem from './OverviewLineItem'
import styles from './styles.module.scss'

export interface ApplicationOverviewProps {
  creditApplication: GetCreditApplicationDetails_creditApplication_CreditApplication
}

const ApplicationOverview = ({ creditApplication }: ApplicationOverviewProps) => {
  const [getClassName] = useNameFormatter('ApplicationOverview')

  const { authorizedSigner, fundableLimit, shouldBeExpiredAt, status, statusCategory, startedAt, bdcOrgId } =
    creditApplication ?? {}

  const { email, firstName, lastName } = authorizedSigner ?? {}

  const dashboardStatusTagProps = getDashboardStatusTagProps(statusCategory ?? StatusCategory.INCOMPLETE)

  const authorizedSignerValue = firstName && lastName ? `${firstName} ${lastName}` : email

  if (!creditApplication) {
    return <Spinner centered />
  }

  return (
    <div className={styles[getClassName('')]}>
      <OverviewLineItem
        dataTestId='status'
        icon='todo'
        label={
          <FormattedMessage
            defaultMessage='Status:'
            id='sputnik.ApplicationOverview__tzMNF3'
          />
        }
        value={status && <Tag {...dashboardStatusTagProps} />}
      />
      <OverviewLineItem
        dataTestId='started-on-date'
        icon='calendar'
        label={
          <FormattedMessage
            defaultMessage='Started on:'
            id='sputnik.ApplicationOverview__Z5cU8c'
          />
        }
        value={startedAt && formatDate(startedAt)}
      />
      <OverviewLineItem
        dataTestId='expires-on-date'
        icon='date'
        label={
          <FormattedMessage
            defaultMessage='Expires on:'
            id='sputnik.ApplicationOverview__0t4WRs'
          />
        }
        value={shouldBeExpiredAt && formatDate(shouldBeExpiredAt)}
      />
      <OverviewLineItem
        dataTestId='authorized-signer'
        icon='signature'
        label={
          <FormattedMessage
            defaultMessage='Authorized signer:'
            id='sputnik.ApplicationOverview__2DRSsN'
          />
        }
        value={authorizedSignerValue}
      />
      <OverviewLineItem
        dataTestId='line-of-credit-offered'
        icon='cash'
        label={
          <FormattedMessage
            defaultMessage='Line of credit offered:'
            id='sputnik.ApplicationOverview__wKVwsU'
          />
        }
        value={fundableLimit && <FormattedCurrency value={fundableLimit} />}
      />
      <OverviewLineItem
        dataTestId='linked-ap-account'
        icon='profile'
        label={
          <FormattedMessage
            defaultMessage='Linked to {BILL} AP:'
            id='sputnik.ApplicationOverview__DGk70a'
            values={{
              BILL: PRODUCT_CONST_STRINGS.BILL,
            }}
          />
        }
        subLabel={
          bdcOrgId && (
            <FormattedMessage
              defaultMessage='(Org ID: {bdcOrgId})'
              id='sputnik.ApplicationOverview__6xM5FT'
              values={{ bdcOrgId }}
            />
          )
        }
        value={
          <FormattedMessage
            defaultMessage='{bdcOrgId, select, true {Yes} other {No}}'
            id='sputnik.ApplicationOverview__a5msuh'
            values={{
              bdcOrgId: !!bdcOrgId,
            }}
          />
        }
      />
    </div>
  )
}

export default ApplicationOverview
